import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Search,
  Loader2,
  Building2,
  Mail,
  Phone,
  MapPin,
  ShoppingCart,
  Coins,
  TrendingUp,
  ArrowRight,
  MessageSquare,
} from 'lucide-react';
import api from '../lib/api/client';
import { useFormatters } from '../contexts/PreferencesContext';
import { useAuth } from '../contexts/AuthContext';
import NDAStatusControl from '../components/onboarding/NDAStatusControl';
import CustomerChatDialog from '../components/suppliers/CustomerChatDialog';

interface Customer {
  id: number;
  name: string;
  company_type: string;
  primary_email: string;
  primary_phone: string;
  city: string;
  country: string;
  po_count?: number;
  total_value?: number;
  open_lines?: number;
}

interface CustomerStats {
  total_customers: number;
  total_po_value: number;
  open_po_lines: number;
  total_pos: number;
}

export default function Customers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [stats, setStats] = useState<CustomerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [chatCustomer, setChatCustomer] = useState<Customer | null>(null);

  const { formatCurrency: _formatCurrency } = useFormatters();
  const { user } = useAuth();
  const [supplierCurrency, setSupplierCurrency] = useState('GBP');

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoading(true);
        // CR-5867: the response includes a top-level `currency` field driven
        // by the supplier company's base_currency, so the symbol and the
        // converted totals stay coherent across multi-country customers.
        const response = await api.get('/core/my-customers/');

        const customersData = response.data.results || [];
        if (response.data.currency) {
          setSupplierCurrency(response.data.currency);
        }
        setCustomers(customersData);

        const totalStats: CustomerStats = {
          total_customers: customersData.length,
          total_po_value: customersData.reduce((sum: number, c: Customer) => sum + (c.total_value || 0), 0),
          open_po_lines: customersData.reduce((sum: number, c: Customer) => sum + (c.open_lines || 0), 0),
          total_pos: customersData.reduce((sum: number, c: Customer) => sum + (c.po_count || 0), 0),
        };
        setStats(totalStats);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load customers';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, []);

  // CR-8605: a customer's message bell links to /customers?messages=<customer id>.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedCustomerId = Number(searchParams.get('messages')) || null;
  useEffect(() => {
    if (!linkedCustomerId) return;
    const linked = customers.find((c) => c.id === linkedCustomerId);
    if (linked) setChatCustomer(linked);
  }, [linkedCustomerId, customers]);

  const closeChat = () => {
    setChatCustomer(null);
    if (searchParams.has('messages')) {
      const next = new URLSearchParams(searchParams);
      next.delete('messages');
      setSearchParams(next, { replace: true });
    }
  };

  const filteredCustomers = customers.filter((customer) =>
    customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    customer.city?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    customer.country?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const CURRENCY_SYMBOLS: Record<string, string> = {
    GBP: '£', USD: '$', EUR: '€', PLN: 'PLN ', CAD: 'CA$', AUD: 'A$',
  };
  const formatCurrency = (value: number) => {
    const symbol = CURRENCY_SYMBOLS[supplierCurrency] || supplierCurrency + ' ';
    return `${symbol}${value.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading customers...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{error}</p>
            <Button onClick={() => window.location.reload()} className="mt-4">
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Customers</h1>
          <p className="text-muted-foreground">
            Companies that purchase from your company
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Building2 className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Customers</p>
                  <p className="text-2xl font-bold">{stats.total_customers}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <ShoppingCart className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Orders</p>
                  <p className="text-2xl font-bold">{stats.total_pos}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Coins className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Order Value</p>
                  <p className="text-2xl font-bold">{formatCurrency(stats.total_po_value)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-orange-100 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Open Lines</p>
                  <p className="text-2xl font-bold">{stats.open_po_lines}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search customers by name, city, or country..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      {/* Customer List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredCustomers.map((customer) => (
          <Card key={customer.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    {/* CR-6250: the name opens the customer's profile. A real
                        button, matching the Organisation Structure node, so it
                        is keyboard reachable. */}
                    <CardTitle className="text-lg">
                      <button
                        type="button"
                        className="text-left hover:text-primary hover:underline"
                        title={`Open ${customer.name}`}
                        onClick={() => navigate(`/companies/${customer.id}`)}
                      >
                        {customer.name}
                      </button>
                    </CardTitle>
                    <Badge variant="outline" className="mt-1">
                      Customer
                    </Badge>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {customer.primary_email && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Mail className="h-4 w-4" />
                  <span className="truncate">{customer.primary_email}</span>
                </div>
              )}
              {customer.primary_phone && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Phone className="h-4 w-4" />
                  <span>{customer.primary_phone}</span>
                </div>
              )}
              {(customer.city || customer.country) && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  <span>
                    {[customer.city, customer.country].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}

              <div className="pt-3 border-t flex items-center justify-between">
                <div className="text-sm flex items-center gap-3 flex-wrap">
                  <span>
                    <span className="text-muted-foreground">Orders: </span>
                    <span className="font-medium">{customer.po_count || 0}</span>
                  </span>
                  {/* V12: sign the customer's NDA once, here, not per RFQ */}
                  <NDAStatusControl customerId={customer.id} customerName={customer.name} />
                </div>
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setChatCustomer(customer)}
                    className="text-primary"
                  >
                    <MessageSquare className="h-4 w-4 mr-1" />
                    Messages
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/purchasing?customer=${customer.id}`)}
                    className="text-primary"
                  >
                    View Orders
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredCustomers.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              {searchQuery
                ? 'No customers found matching your search.'
                : 'No customers found.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Messages Dialog */}
      {chatCustomer && user?.company && (
        <CustomerChatDialog
          supplierId={user.company}
          customerId={chatCustomer.id}
          customerName={chatCustomer.name}
          onClose={closeChat}
        />
      )}
    </div>
  );
}
